import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import cn from 'classnames';

import type { AppState } from 'index';

import { useContacts } from 'hooks/useContacts';
import { getTestIdByParts } from 'utils/getTestIdByParts';
import type { ContactMethod } from 'types/app';

import { PreferredContactWayEnum } from '@Modules/offers/constants';
import { getOfferContactActions } from '@Modules/offers/slice';

import { Button } from '@Atomic/atoms/Button';
import type { ButtonPresentationType } from '@Atomic/atoms/Button/types';

import styles from './sellerContact.module.scss';
import type { ContactItem, SellerContactProps } from './types';

const PRESENTATIONS: ButtonPresentationType[] = ['filled', 'tonal', 'outline'];

export const SellerContact = ({
  className,
  buttonClassName,
  offerId,
  seller,
  contactViaApp,
  testId = 'seller_contact',
}: SellerContactProps) => {
  const { t } = useTranslation();
  const isContactRequestLoading = useSelector(
    (state: AppState) => state.loading[`${getOfferContactActions.name}_${offerId}`]
  );
  const { openPhoneModal, openTelegram, startChat } = useContacts(offerId, {
    contactViaApp,
  });
  const [pendingMethod, setPendingMethod] = useState<ContactMethod | null>(null);

  useEffect(() => {
    if (!isContactRequestLoading) {
      setPendingMethod(null);
    }
  }, [isContactRequestLoading]);

  const handleModalOpen = useCallback(() => {
    // The contact request may be replaced with the login modal (BP-7440) or a
    // store redirect (BP-7657) — show the loader only when the request has
    // actually started
    if (openPhoneModal()) {
      setPendingMethod('phone');
    }
  }, [setPendingMethod, openPhoneModal]);

  const handleTelegramOpen = useCallback(() => {
    if (openTelegram()) {
      setPendingMethod('telegram');
    }
  }, [setPendingMethod, openTelegram]);

  const handleChatOpen = useCallback(() => startChat(), [startChat]);

  const orderedContacts = useMemo<ContactItem[]>(() => {
    const items: ContactItem[] = [];

    if (seller.chatAllowed) {
      items.push({ type: 'chat' });
    }

    [...new Set(seller.preferredContactWay)]
      .sort((a, b) => a - b)
      .forEach((way) => items.push({ type: way }));

    return items;
  }, [seller.chatAllowed, seller.preferredContactWay]);

  return (
    <div
      className={cn(styles.contacts, className)}
      data-testid={testId}
      aria-label={t('offer.contact')}
      role="group"
    >
      {orderedContacts.map((contact, index) => {
        const presentation = PRESENTATIONS[index] ?? 'outline';

        if (contact.type === 'chat') {
          return (
            <Button
              key="chat"
              className={cn(styles.button, buttonClassName)}
              testId={getTestIdByParts(testId, 'chat')}
              onClick={handleChatOpen}
              intent="accent"
              presentation={presentation}
              fullWidth
            >
              {t('buttons.chatBirBir')}
            </Button>
          );
        }

        if (contact.type === PreferredContactWayEnum.PHONE) {
          const isPhoneLoading = pendingMethod === 'phone';

          return (
            <Button
              key="phone"
              loading={isPhoneLoading}
              disabled={isPhoneLoading}
              testId={getTestIdByParts(testId, 'phone')}
              onClick={handleModalOpen}
              className={cn(styles.button, buttonClassName)}
              intent="accent"
              presentation={presentation}
              fullWidth
            >
              {t('phone')}
            </Button>
          );
        }

        if (contact.type === PreferredContactWayEnum.TELEGRAM) {
          const isTelegramLoading = pendingMethod === 'telegram';

          return (
            <Button
              key="telegram"
              className={cn(styles.button, buttonClassName)}
              loading={isTelegramLoading}
              disabled={isTelegramLoading}
              testId={getTestIdByParts(testId, 'telegram')}
              onClick={handleTelegramOpen}
              intent="accent"
              presentation={presentation}
              fullWidth
            >
              {t('buttons.telegram')}
            </Button>
          );
        }

        return null;
      })}
    </div>
  );
};
